import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { configData } from "../config";
import { Button, Container, Form, Stack } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import TwoStageLayoutHeader from "../../src/shared/components/TwoStageLayoutHeader";
import {
  getSubscriptionVoucher,
  selectSubscriptionVoucherById,
} from "../../src/DonationSubscription/store/getAllSubscriptionVoucherSlice";
import DonationSubscriptionAllInvoiceDefault from "./components/donation_subscription_invoice/DonationSubscriptionAllInvoiceDefault";
import { UilPrint, UilCopy } from "@iconscout/react-unicons";
import InvoiceLayout from "../shared/components/invoiceLayout";
import { setAccessToken, setTenant } from "../Auth/store/authSlice";
import errorImage from '../assets/images/error.png';
import { fetchSetting } from "../Settings/store/settingSlice";

const DonationSubscriptionAllInvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [donationInvoice, setDonationInvoice] = useState(null);
  const [voucherUrl, setVoucherUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleCopyClick = () => {
    if (voucherUrl) {
      navigator.clipboard
        .writeText(voucherUrl)
        .then(() => {
          setCopied(true);
        })
        .catch((err) => {
          console.error("Failed to copy URL:", err);
        });
    }
  };

  const accessToken = useSelector((state) => state?.auth?.token);
  const tenantId = useSelector((state) => state?.auth?.tenantId);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedValue = params.get("data");
    if (encryptedValue) {
      decryptData(encryptedValue);
    } else {
      setShowError(true);
    }
  }, []);

  const decryptData = async (formData) => {
    try {
      const response = await axios.post(
        `${configData.API_URL}tenants/verify-donation-invoices`,
        { data: formData }
      );

      dispatch(setTenant(response.data?.tenant?.id));
      dispatch(setAccessToken(response.data?.decryptedData?.access_token));

      if (response.data.voucher) {
        setDonationInvoice(response.data.voucher);
        setVoucherUrl(response?.data?.voucher?.url);
      } else if (response.data.voucher) {
        setDonationInvoice([response.data.voucher]);
      } else {
        setDonationInvoice([]);
      }
    } catch (err) {
      console.error("Error decrypting data:", err);
    }
  };

  useEffect(
    () => {
      dispatch(
        getSubscriptionVoucher({
          id: donationInvoice?.id,
          token: accessToken,
          tenantId: tenantId,
        })
      );
    },
    donationInvoice?.id,
    accessToken,
    tenantId
  );

  useEffect(() => {
    dispatch(
      fetchSetting({
        accessToken: accessToken,
        tenant_id: tenantId,
      })
    );
  }, [tenantId, accessToken]);

  let subscriptionTabs;
  subscriptionTabs = [
    {
      key: "1",
      title: "Default",
      component: (
        <DonationSubscriptionAllInvoiceDefault
          name="Subscription"
          id={id}
          data={donationInvoice}
        />
      ),
    },
  ];

  const componentRef = useRef();

  const handlePrint = () => {
    html2canvas(componentRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4", true);

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      const timeStamp = Date.now();
      const filename = `donation_invoice_${timeStamp}.pdf`;
      const pdfBlob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl);
    });
  };

  const handleReturnToGrid = () => {
    navigate(-1);
  };

  const header = (
    <TwoStageLayoutHeader
      ItemActions={
        <Stack
          gap={3}
          direction="horizontal"
          className="mt-2 mb-2 ml-3 float-end"
        >
          <Button
            variant="outline-secondary"
            onClick={handleCopyClick}
            disabled={copied}
          >
            <UilCopy className="mx-2 mb-1" size="16" />
            {copied ? "Copied!" : "Copy Url"}
          </Button>

          <Button variant="outline-secondary" onClick={handlePrint}>
            <UilPrint className="mx-2 mb-1" size="16" />
            Print Pdf
          </Button>
        </Stack>
      }
      returnToGrid={handleReturnToGrid}
      adjustment="subscription"
    />
  );

   if (showError) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
                <div className="text-center p-4 border rounded shadow-sm bg-white">
                    <img
                        src={errorImage}
                        alt="Error"
                        style={{ width: "80px", marginBottom: "20px" }}
                    />
                    <h3 className="text-danger mb-3">Invalid or Missing Link</h3>
                    <p className="text-muted">
                        The URL you used is missing a required parameter or is not valid.
                    </p>
                </div>
            </div>
        )
    }

  return (
    <>
      <ToastContainer />
      <Container fluid>
        <InvoiceLayout
          tabs={subscriptionTabs}
          currentItemId={id}
          returnToGrid={handleReturnToGrid}
          print={componentRef}
          header={header}
        />
      </Container>
    </>
  );
};

export default DonationSubscriptionAllInvoiceView;
